/**
 * 👹 CHAOTIC GREMLIN TEMPERAMENT
 * Mischief builds to TOTAL MAYHEM!
 * Don't feed it after midnight...
 *
 * Created by Tom & Azura - January 14, 2026
 */

import type { Temperament } from '../types';

export const ChaoticGremlin: Temperament = {
  id: 'chaotic-gremlin',
  name: 'Chaotic Gremlin',
  emoji: '👹',
  description: 'Scheming mischief builds to TOTAL MAYHEM! Pure chaos unleashed!',

  // ============================================================
  // MOODS - Building toward MAYHEM
  // ============================================================

  moods: ['scheming', 'mischievous', 'troublemaking', 'unhinged', 'mayhem', 'crashed'],
  initialMood: 'scheming',

  // ============================================================
  // MOOD TRANSITIONS
  // ============================================================

  moodMatrix: {
    scheming: [
      { mood: 'scheming', weight: 0.5 },
      { mood: 'mischievous', weight: 0.5 },
    ],
    mischievous: [
      { mood: 'scheming', weight: 0.1 },
      { mood: 'mischievous', weight: 0.35 },
      { mood: 'troublemaking', weight: 0.55 },
    ],
    troublemaking: [
      { mood: 'mischievous', weight: 0.1 },
      { mood: 'troublemaking', weight: 0.35 },
      { mood: 'unhinged', weight: 0.55 },
    ],
    unhinged: [
      { mood: 'troublemaking', weight: 0.1 },
      { mood: 'unhinged', weight: 0.35 },
      { mood: 'mayhem', weight: 0.55 },
    ],
    mayhem: [
      { mood: 'mayhem', weight: 0.6 },
      { mood: 'crashed', weight: 0.4 },
    ],
    crashed: [
      { mood: 'crashed', weight: 0.25 },
      { mood: 'scheming', weight: 0.65 },
      { mood: 'mischievous', weight: 0.1 },
    ],
  },

  // ============================================================
  // MOOD DURATIONS
  // ============================================================

  moodDurations: {
    scheming:      { base: 2200, variance: 2800 },
    mischievous:   { base: 400, variance: 600 },
    troublemaking: { base: 300, variance: 450 },
    unhinged:      { base: 250, variance: 350 },
    mayhem:        { base: 180, variance: 280 },
    crashed:       { base: 1000, variance: 1500 },
  },

  moodConstraints: {
    mayhem: {
      minDuration: 2000,
      maxDuration: 5000,
    },
  },

  // ============================================================
  // ENERGY SYSTEM - Builds chaos energy!
  // ============================================================

  energy: {
    max: 100,
    initial: 0,
    regenRate: 8,

    moodDrain: {
      scheming:      -4,   // Planning chaos...
      mischievous:   -3,   // Small pranks building
      troublemaking: -2,   // Getting wilder...
      unhinged:      -1,   // Almost there...
      mayhem:        32,   // CHAOS BURNS HOT!
      crashed:       -6,   // Chaos hangover
    },

    thresholds: {
      exhausted: 8,
      low: 22,
      high: 50,
      explosive: 68,
    },

    restingMoods: ['scheming', 'crashed', 'mischievous', 'troublemaking'],
  },

  // ============================================================
  // EXPRESSIONS
  // ============================================================

  expressions: {
    scheming: {
      rotation: { x: -20, y: -25, z: -3 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 0.98,
      transitionMs: 300,
      jitter: 2,
      glowIntensity: 0.2,
      glowColor: 'rgba(100, 255, 100, 0.4)',
    },
    mischievous: {
      rotation: { x: -18, y: -25, z: 3 },
      translation: { x: 1, y: -1, z: 0 },
      scale: 1,
      transitionMs: 150,
      jitter: 4,
      glowIntensity: 0.35,
      glowColor: 'rgba(150, 255, 100, 0.5)',
    },
    troublemaking: {
      rotation: { x: -20, y: -25, z: -2 },
      translation: { x: -1, y: 0, z: 0 },
      scale: 1.01,
      transitionMs: 100,
      jitter: 7,
      glowIntensity: 0.5,
      glowColor: 'rgba(255, 200, 50, 0.6)',
    },
    unhinged: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: -1, z: 0 },
      scale: 1.03,
      transitionMs: 60,
      jitter: 10,
      glowIntensity: 0.7,
      glowColor: 'rgba(255, 150, 50, 0.75)',
    },
    mayhem: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: -2, z: 0 },
      scale: 1.06,
      transitionMs: 40,
      jitter: 9,
      glowIntensity: 1,
      glowColor: 'rgba(255, 50, 150, 1)',
      cssClass: 'mayhem',
    },
    crashed: {
      rotation: { x: -25, y: -25, z: 5 },
      translation: { x: 2, y: 4, z: 0 },
      scale: 0.95,
      transitionMs: 500,
      jitter: 0.5,
      glowIntensity: 0.1,
      glowColor: 'rgba(100, 100, 100, 0.3)',
    },
  },

  // ============================================================
  // CORNER BOUNCING (for mayhem mode - CHAOTIC!)
  // ============================================================

  corners: {
    center: {
      transform: { x: 0, y: 0, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      bulge: { x: 50, y: 50 },
    },
    topLeft: {
      transform: { x: -3, y: 4, z: -2 },
      translation: { x: -6, y: -5, z: 0 },
      bulge: { x: 15, y: 15 },
    },
    topRight: {
      transform: { x: -3, y: -4, z: 2 },
      translation: { x: 6, y: -5, z: 0 },
      bulge: { x: 85, y: 15 },
    },
    bottomLeft: {
      transform: { x: 3, y: 4, z: 2 },
      translation: { x: -6, y: 5, z: 0 },
      bulge: { x: 15, y: 85 },
    },
    bottomRight: {
      transform: { x: 3, y: -4, z: -2 },
      translation: { x: 6, y: 5, z: 0 },
      bulge: { x: 85, y: 85 },
    },
  },

  bouncingMoods: ['mayhem', 'unhinged'],

  bounceIntervals: {
    mayhem: 100,
    unhinged: 180,
  },

  // ============================================================
  // STIMULI HANDLERS
  // ============================================================

  stimulusHandlers: {
    time: (_stimulus, currentMood, energy) => {
      if (currentMood === 'scheming' && energy < 15) {
        return { energyDelta: 5 };
      }
      if (currentMood === 'scheming' && energy >= 15) {
        return { transitionTo: 'mischievous', energyDelta: 4 };
      }
      if (currentMood === 'mischievous') {
        return { transitionTo: 'troublemaking', energyDelta: 3 };
      }
      return null;
    },

    poke: (stimulus, currentMood, energy) => {
      const intensity = stimulus.intensity || 1;

      // Poking FEEDS the chaos!
      if (currentMood === 'scheming') {
        return { transitionTo: 'mischievous', energyDelta: 22 * intensity };
      }
      if (currentMood === 'mischievous') {
        return { transitionTo: 'troublemaking', energyDelta: 22 * intensity };
      }
      if (currentMood === 'troublemaking') {
        return { transitionTo: 'unhinged', energyDelta: 25 * intensity };
      }
      if (currentMood === 'unhinged') {
        return { transitionTo: 'mayhem', energyDelta: 30 * intensity, forceDuration: 2000 };
      }
      if (currentMood === 'crashed') {
        return { transitionTo: 'mischievous', energyDelta: 18 * intensity };
      }
      return { energyDelta: 18 * intensity };
    },

    shake: (stimulus, currentMood, energy) => {
      const intensity = stimulus.intensity || 1;

      // Shaking = MAXIMUM CHAOS FUEL!
      if (currentMood === 'scheming' || currentMood === 'mischievous') {
        return { transitionTo: 'unhinged', energyDelta: 40 * intensity };
      }
      if (currentMood === 'troublemaking' || currentMood === 'unhinged' || energy > 35) {
        return { transitionTo: 'mayhem', energyDelta: 45 * intensity, forceDuration: 2800 };
      }
      return { transitionTo: 'unhinged', energyDelta: 35 * intensity };
    },
  },

  // ============================================================
  // MOOD INDICATORS
  // ============================================================

  moodIndicators: {
    scheming: {
      emoji: '😈',
      text: '...',
      label: '*plotting mischief*',
    },
    mischievous: {
      emoji: '😏',
      text: 'heh',
      label: '*giggling*',
    },
    troublemaking: {
      emoji: '🤪',
      text: '?!',
      label: '*cackling*',
    },
    unhinged: {
      emoji: '🤡',
      text: '!!',
      label: 'LOSING CONTROL',
    },
    mayhem: {
      emoji: '👹',
      text: '!!!',
      label: '💥 TOTAL MAYHEM! 💥',
    },
    crashed: {
      emoji: '😵',
      text: 'zzz',
      label: '*chaos hangover*',
    },
  },
};
